import { Trans, useTranslation } from 'react-i18next'
import moment from 'moment'
import { RouteComponentProps } from 'react-router'
import styled from 'styled-components'
import { toNumber } from 'lodash'
import { Userpilot } from 'userpilot'
import useCurrentSession from '@/hooks/use-current-session'
import AreaTitle from '@/components/area-title'
import { Button } from '@/components/button'
import Page from '@/components/page'
import Panel from '@/components/panel'
import Skeleton from '@/components/skeleton'
import { ParagraphMedium } from '@/components/text'
import { showBillingInfoModal } from '@/features/Settings/PlanSettings/billing-info-modal'
import { colors, breakpoints } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import config from '@/config'
import StatusBadge from '@/components/status-badge'
import { Box } from '@/components/structure'
import { useFlag } from '@/flags'
import { useAppWebView } from '@/hooks/use-app-webview'
import { PaymentMethod } from './use-payment-methods'
import { CardSummary } from './payment-summary'
import { Plan, parsePlan, AccountType, BillingType } from './use-plan'

const { WEB_URL } = config
const pricingPageUrl = `${WEB_URL}/pricing`

const ContentWrapper = styled.div`
  position: relative;
  max-width: 748px;
  width: 100%;
  margin: auto;
  padding: 0 16px;
  flex: 1;
`

const StyledPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    padding: 24px;
    flex-direction: column;
    align-items: space-between;
  }
`

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    align-items: center;
    margin-bottom: 24px;
  }
`

const Label = styled.p`
  width: 200px;
  color: ${colors.grey16};
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  margin: 0 0 8px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    width: unset;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: auto;
    margin-bottom: 16px;
    text-align: center;
  }
`

const Description = styled(ParagraphMedium)`
  flex: 1;
  color: ${colors.grey56};
  margin-right: 64px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    margin-right: unset;
  }
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    text-align: center;
    margin: 0;
  }
`

const LoadingPanel = () => (
  <StyledPanel hSpace={0} vSpace={0}>
    <Row>
      <LabelWrapper>
        <Skeleton height={20} width={200} bottom={12} />
        <Skeleton height={16} width={300} />
      </LabelWrapper>
      <Skeleton height={52} width={160} />
    </Row>
  </StyledPanel>
)

type PlanSettingsDashboardProps = RouteComponentProps<Nothing> & {
  card?: PaymentMethod
  activePlan?: Plan
  nextPlan?: Plan
  isLoading?: boolean
}

type ParsePlan = {
  name: AccountType | 'none'
  freq: BillingType
  date: string
}

const formatDate = (date: string) => moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD')

const simplePlan = (plan?: Plan, isNext?: boolean): ParsePlan => {
  if (!plan) return { name: 'none', freq: 'none', date: '' }
  return {
    name: plan?.account_type,
    freq: plan?.billing_type,
    date: formatDate(isNext ? plan?.start_date : plan?.charged_through_date)
  }
}

const usePlanText = (
  activePlan?: Plan,
  nextPlan?: Plan,
  isBillingUser?: boolean,
  isSpecialPlan?: boolean
) => {
  const { t } = useTranslation()
  const planNames: Record<string, string> = {
    essentials: t('b.auth.create.essentials.label', 'Essentials'),
    growth: t('b.auth.create.growth.label', 'Growth'),
    business: t('b.auth.create.business.label', 'Business'),
    special: t('b.auth.create.enterprise.label', 'Enterprise')
  }
  const formatFreq = (key: BillingType) =>
    key === 'annual'
      ? t('l.plan_settings.view.plan_frequency_annual.text', 'annually')
      : t('l.plan_settings.view.plan_frequency_monthly.text', 'monthly')
  const formatName = (key: AccountType) => {
    return planNames[key]
  }
  const { name, date, freq } = simplePlan(activePlan)
  const { name: newName, date: newDate, freq: newFreq } = simplePlan(nextPlan, true)
  let summary: React.ReactNode = ''
  let payment = { paymentDate: '', requiresPayment: false }
  if (!isBillingUser || isSpecialPlan || name === 'special') {
    const key = name !== 'none' ? name : 'essentials'
    const planName = formatName(!isSpecialPlan ? key : 'special')
    summary = (
      <Trans
        t={t}
        key="l.plan_settings.generic_plan.text"
        i18nKey="l.plan_settings.generic_plan.text"
        values={{ planName }}
      >
        <strong>{{ planName }}</strong> Plan
      </Trans>
    )
  } else if ((name === 'none' || name === 'essentials') && newName === 'none') {
    const planName = formatName('essentials')
    summary = (
      <Trans
        t={t}
        key="l.plan_settings.essentials_plan_info.text"
        i18nKey="l.plan_settings.essentials_plan_info.text"
        values={{ planName }}
      >
        <strong>{{ planName }}</strong> (free)
      </Trans>
    )
  } else if (name !== 'none' && (newName === 'none' || (name === newName && freq === newFreq))) {
    const planName = formatName(name)
    const frequency = formatFreq(freq)
    payment = { paymentDate: date, requiresPayment: true }
    summary = (
      <Trans
        t={t}
        key="l.plan_settings.current_plan_info.text"
        i18nKey="l.plan_settings.current_plan_info.text"
        values={{ planName, frequency, date }}
      >
        <strong>{{ planName }}</strong> billed {{ frequency }}. Your next invoice is due on{' '}
        {{ date }}.
      </Trans>
    )
  } else if (name !== 'none' && newName === 'essentials') {
    const planName = formatName(name)
    const frequency = formatFreq(freq)
    summary = (
      <Trans
        t={t}
        key="l.plan_settings.downgrade_to_free.text"
        i18nKey="l.plan_settings.downgrade_to_free.text"
        values={{ planName, frequency, newDate }}
      >
        <strong>{{ planName }}</strong> billed {{ frequency }}. Your requested plan change to
        Essentials will take effect on {{ newDate }}.
      </Trans>
    )
  } else if (name !== 'none' && newName !== 'none') {
    const planName = formatName(name)
    const frequency = formatFreq(freq)
    const newPlanFreq = freq !== newFreq ? ` (${formatFreq(newFreq)})` : ''
    const newPlanName = formatName(newName) + newPlanFreq
    payment = { paymentDate: newDate, requiresPayment: true }
    summary = (
      <Trans
        t={t}
        key="l.plan_settings.current_plan_change.text"
        i18nKey="l.plan_settings.current_plan_change.text"
        values={{ planName, frequency, newPlanName, newDate }}
      >
        <strong>{{ planName }}</strong> billed {{ frequency }}. Your requested plan change to{' '}
        {{ newPlanName }} will take effect and the invoice due date will be {{ newDate }}.
      </Trans>
    )
  } else if (name === 'none' && newName !== 'none') {
    const planName = formatName('essentials')
    const frequency = formatFreq(freq)
    const newPlanFreq = freq !== newFreq ? ` (${formatFreq(newFreq)})` : ''
    const newPlanName = formatName(newName) + newPlanFreq
    payment = { paymentDate: newDate, requiresPayment: true }
    summary = (
      <Trans
        t={t}
        key="l.plan_settings.current_plan_change.text"
        i18nKey="l.plan_settings.current_plan_change_2.text"
        values={{ planName, frequency, newPlanName, newDate }}
      >
        <strong>{{ planName }}</strong>. Your requested plan change to {{ newPlanName }} will take
        effect and the invoice due date will be {{ newDate }}.
      </Trans>
    )
  }
  return { summary, ...payment }
}

const PlanSettingsDashboard: React.FC<PlanSettingsDashboardProps> = ({
  history,
  activePlan,
  nextPlan,
  card,
  isLoading
}) => {
  const taxFlag = useFlag(['features', 'pricing', 'taxCode'], false)
  const { t } = useTranslation()
  const hasPaymentMethod = !isNullOrEmpty(card)
  const session = useCurrentSession()
  const billingUserId = session.company_info.billing_user_id
  const userId = session.id
  const isBillingUser = toNumber(userId) === toNumber(billingUserId)
  const basePlanName = session?.company_info?.base_plan_name
  const [planType, billingType] = parsePlan(basePlanName)
  const isSpecialPlan = planType === 'special'
  const freePlan = billingType === 'none' && !isSpecialPlan
  const hasPlan = !!basePlanName && !freePlan
  const canEdit = isBillingUser && !isSpecialPlan
  const {
    summary: text,
    requiresPayment,
    paymentDate
  } = usePlanText(activePlan, nextPlan, isBillingUser, isSpecialPlan)

  const warnPayment = requiresPayment && !hasPaymentMethod
  const isApp = useAppWebView()
  const { location } = history
  const query = location.search
  const handlePricing = () => {
    Userpilot.track('app_upgrade_settings_planbilling_view_btn')
    window.open(pricingPageUrl)
  }
  return (
    <Page>
      <ContentWrapper>
        <AreaTitle>
          {t(
            'l.settings.view.plan_settings.description',
            'Review your plan details and payment method'
          )}
        </AreaTitle>
        {isLoading ? (
          <>
            <LoadingPanel />
            {canEdit && <LoadingPanel />}
          </>
        ) : (
          <>
            <StyledPanel hSpace={0} vSpace={0}>
              <Row>
                <LabelWrapper>
                  <Label>{t('l.plan_settings.current_plan.label', 'Current Plan')}</Label>
                  <Description>{text}</Description>
                  {warnPayment && (
                    <Box top={12}>
                      <StatusBadge
                        type="error"
                        title={t('r.payment_settings.view.payment_issue.text', 'Payment Issue')}
                        tooltip={t('r.payment_settings.view.payment_issue.tooltip', {
                          defaultValue:
                            'Your next invoice is due on {{value}} but you do not have a valid card on file.',
                          value: paymentDate
                        })}
                        toolTipIcon={true}
                      />
                    </Box>
                  )}
                </LabelWrapper>
                {!isApp && (
                  <Button theme={hasPlan ? 'secondary' : 'primary'} onClick={handlePricing}>
                    {hasPlan
                      ? t('l.plan_settings.view.view_plan.button', 'View')
                      : t('l.plan_settings.view.compare_plan.button', 'Compare Plans')}
                  </Button>
                )}
              </Row>
            </StyledPanel>
            {canEdit && (
              <StyledPanel hSpace={0} vSpace={0}>
                <Row>
                  <LabelWrapper>
                    <Label>
                      {t('l.plan_settings.view.payment_method.label', 'Payment Method')}
                    </Label>
                    {hasPaymentMethod ? (
                      <Description>
                        <CardSummary card={card} />
                      </Description>
                    ) : (
                      <Description>
                        {warnPayment ? (
                          <Box as="span" color={colors.alertNegative}>
                            {t(
                              'l.plan_settings.view.payment_method_warn.text',
                              'Please add a credit card before your next invoice date to keep your plan active.'
                            )}
                          </Box>
                        ) : (
                          t('l.plan_settings.view.payment_method.text', 'No payment method found.')
                        )}
                      </Description>
                    )}
                  </LabelWrapper>
                  {hasPaymentMethod ? (
                    <Button
                      theme="secondary"
                      to={`/settings/plan-settings/payment/view${query}`}
                      disabled={!canEdit}
                    >
                      {t('l.plan_settings.view.view_payment_method.button', 'Manage Card')}
                    </Button>
                  ) : (
                    <Button
                      theme="primary"
                      disabled={!canEdit}
                      onClick={() => showBillingInfoModal({ taxFlag })}
                    >
                      {t('l.plan_settings.view.connect_payment_method.button', 'Add Card')}
                    </Button>
                  )}
                </Row>
              </StyledPanel>
            )}
          </>
        )}
      </ContentWrapper>
    </Page>
  )
}

export default PlanSettingsDashboard
